import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { Link } from "~/components/RouterLink";
import styles from "./orthodontics-for-children.module.css";
import { useEffect, useRef, useState } from "react";
import { OptimizedImage } from "~/components/OptimizedImage";

type LinkPart = {
  text: string;
  to: string;
};

type RichParagraph = Array<string | LinkPart>;

type FaqItem = {
  question: string;
  answers: string[];
};

type Localization = {
  title: string;
  introHeading: string;
  introParagraphs: RichParagraph[];
  checkupHeading: string;
  checkupParagraphs: RichParagraph[];
  earlyTreatmentHeading: string;
  earlyTreatmentParagraphs: string[];
  timingHeading: string;
  timingParagraphs: string[];
  timingList: string[];
  signsHeading: string;
  signsParagraphs: string[];
  signsList: string[];
  consultationHeading: string;
  consultationParagraphs: RichParagraph[];
  faqHeading: string;
  faqItems: FaqItem[];
  closingHeading: string;
  closingParagraph: string;
  inlineImageAlt: string;
};

const englishLocalization: Localization = {
  title: "Orthodontics for Children",
  introHeading: "Miami’s Best Orthodontist for Children",
  introParagraphs: [
    [
      "When it comes to the appearance and health of your children’s smiles, orthodontic care is essential. In addition to the self-esteem benefits of a healthy smile, early orthodontic care is extremely important for long-term oral health. Orthodontics for children is recommended for children by age 7",
    ],
    [
      "Straight teeth are easier to brush and floss and are less likely to develop gum disease or tooth decay.",
    ],
    [
      "At Miami Orthodontic Specialists, we love kids and are proud to be considered ",
      { text: "Miami’s best orthodontist!", to: "/" },
    ],
  ],
  checkupHeading: "All Kids Should Get an Orthodontic Check-Up By Age 7",
  checkupParagraphs: [
    [
      "Your child needs teeth and jaws that are properly aligned to have a smile that's long-lasting and healthy – in other words, a healthy bite.",
    ],
    [
      "Miami Orthodontic Specialists agrees with the ",
      {
        text: "American Association of Orthodontists",
        to: "https://www.aaoinfo.org/",
      },
      ", who recommends that your child get an orthodontic check-up by no later than age seven years old.",
    ],
    [
      "By then, your child's teeth have developed enough for subtle problems with jaw growth and emerging teeth to still be spotted while some baby teeth are still present. This check-up may reveal that your child's bite is fine and braces for kids aren't necessary.",
    ],
    [
      "Or, Dr. Garcia-Paul may identify a developing problem but recommend monitoring your child's growth and development, and then, if indicated, begin treatment at the appropriate time for your child.",
    ],
    [
      "In other cases, Dr. Garcia-Paul might find a problem that can benefit from early treatment.",
    ],
  ],
  earlyTreatmentHeading: "What Is Early Treatment?",
  earlyTreatmentParagraphs: [
    "Early treatment may prevent or intercept more serious problems from developing and may make treatment at a later age shorter and less complicated.",
    "In some cases, Dr. Garcia-Paul will be able to achieve results that may not be possible once the face and jaws have finished growing.",
  ],
  timingHeading: "Choosing the Best Time for Treatment",
  timingParagraphs: [
    "While the age that kids get braces may vary, orthodontic treatment most often begins between the ages of 9 and 14. Some orthodontic problems are easier to correct if they are treated early.",
    "If Dr. Garcia-Paul thinks your child will need treatment, she'll let you know when it will be best to begin treatment. This gives your orthodontist the best chance to:",
  ],
  timingList: [
    "Guide jaw growth",
    "Lower the risk of trauma to protruded front teeth",
    "Correct harmful oral habits",
    "Improve appearance and self-esteem in your child",
    "Guide permanent teeth into a more favorable position",
    "Create a more pleasing arrangement of teeth, lips and face",
  ],
  signsHeading: "Signs the Bite's Not Right",
  signsParagraphs: [
    "It's not always easy to tell when your child may need braces or has an orthodontic problem. Even teeth that look straight may be hiding a problem bite.",
    "Here are some clues that may indicate the need for orthodontic attention:",
  ],
  signsList: [
    "Early or late loss of baby teeth",
    "Difficulty chewing or biting",
    "Breathing through the mouth",
    "Thumb-sucking",
    "Crowded, misplaced or blocked-out teeth",
    "Jaws that are too far forward or back",
    "Biting the cheek or biting into the roof of the mouth",
    "Protruding teeth",
    "Upper and lower teeth that don't meet, or meet in an abnormal way",
    "An unbalanced facial appearance",
    "Grinding or clenching of the teeth",
  ],
  consultationHeading: "Learn More at Your Free Consultation",
  consultationParagraphs: [
    [
      "If your child has a dental issue, please contact us by setting up a ",
      { text: "free consultation", to: "/free-consult/" },
      ".",
    ],
    [
      "We'd love to discuss your child's orthodontic options and develop a treatment plan that is customized to your child's needs and desires.",
    ],
  ],
  faqHeading: "Frequently Asked Questions About Children's Orthodontic Issues",
  faqItems: [
    {
      question: "When should my child first see an orthodontist?",
      answers: [
        "The American Association of Orthodontists recommends that every child be examined by an orthodontic specialist by age 7.",
        "Early evaluation by an orthodontist can help to ensure that potential problems are detected early and are addressed before they have progressed significantly.",
      ],
    },
    {
      question: "Why would my child need orthodontic treatment so early?",
      answers: [
        "Some orthodontic problems are more easily treated when detected early. At Miami Orthodontic Specialists, we will evaluate what is most appropriate.",
        "In certain cases, interceptive (or Phase I) treatment can prevent your child from needing more invasive treatment when they are older. By age 7, the first permanent molars and incisors have usually erupted into place.",
        "This makes it the perfect time to evaluate whether your child may be developing a crossbite, overbite, underbite, impacted teeth or severe crowding.",
      ],
    },
    {
      question: "What happens if I put off my child's orthodontic treatment?",
      answers: [
        "The problem with waiting for complete eruption of permanent teeth when a child has severe crowding or a jaw discrepancy is the possibility of missing out on growth potential, leading to a compromised result that may not be stable.",
        "Treatment at a later age may require a more aggressive treatment plan to achieve a satisfactory result.",
      ],
    },
    {
      question: "Do all children need orthodontic treatment this early?",
      answers: [
        "No! In fact, most parents are relieved after we evaluate their child and discover that orthodontic treatment is not needed right away.",
        "If your child is not ready for treatment, we will place them in an observation program where they will be examined at regular intervals to ensure that their dental development and facial growth are on track.",
      ],
    },
  ],
  closingHeading:
    "If your child is age 7 or older, call our office today to schedule your complimentary consultation.",
  closingParagraph:
    "We offer this initial examination free of charge because we know how crucial it is for you to fully understand your child’s treatment needs and treatment options before investing in orthodontic care.",
  inlineImageAlt: "Child smiling and holding a watermelon slice with braces",
};

const spanishLocalization: Localization = {
  title: "Ortodoncia para niños",
  introHeading: "El mejor ortodoncista para niños en Miami",
  introParagraphs: [
    [
      "Cuando se trata de la apariencia y la salud de las sonrisas de tus hijos, la atención ortodóntica es esencial. Además de los beneficios de autoestima de una sonrisa saludable, la atención ortodóntica temprana es muy importante para la salud oral a largo plazo. La ortodoncia para niños se recomienda a partir de los 7 años.",
    ],
    [
      "Los dientes rectos son más fáciles de cepillar y usar hilo dental, y tienen menos probabilidades de desarrollar enfermedad de las encías o caries.",
    ],
    [
      "En Miami Orthodontic Specialists, nos encantan los niños y nos enorgullece que nos consideren ",
      { text: "el mejor ortodoncista de Miami.", to: "/" },
    ],
  ],
  checkupHeading:
    "Todos los niños deben tener una revisión ortodóntica a los 7 años",
  checkupParagraphs: [
    [
      "Tu hijo necesita dientes y mandíbulas correctamente alineados para tener una sonrisa duradera y saludable; en otras palabras, una mordida saludable.",
    ],
    [
      "Miami Orthodontic Specialists está de acuerdo con la ",
      {
        text: "American Association of Orthodontists",
        to: "https://www.aaoinfo.org/",
      },
      ", que recomienda que tu hijo tenga una revisión ortodóntica a más tardar a los siete años.",
    ],
    [
      "Para entonces, los dientes de tu hijo se han desarrollado lo suficiente como para detectar problemas sutiles de crecimiento de la mandíbula y de dientes en erupción mientras todavía hay algunos dientes de leche presentes. Esta revisión puede revelar que la mordida de tu hijo está bien y que no necesita brackets.",
    ],
    [
      "O bien, la Dra. Garcia-Paul puede identificar un problema en desarrollo, pero recomendar observar el crecimiento y desarrollo de tu hijo y, si está indicado, comenzar el tratamiento en el momento adecuado.",
    ],
    [
      "En otros casos, la Dra. Garcia-Paul podría encontrar un problema que se beneficie de un tratamiento temprano.",
    ],
  ],
  earlyTreatmentHeading: "¿Qué es el tratamiento temprano?",
  earlyTreatmentParagraphs: [
    "El tratamiento temprano puede prevenir o interceptar problemas más serios antes de que se desarrollen, y puede hacer que el tratamiento a una edad posterior sea más corto y menos complicado.",
    "En algunos casos, la Dra. Garcia-Paul podrá lograr resultados que quizá no sean posibles una vez que la cara y las mandíbulas hayan terminado de crecer.",
  ],
  timingHeading: "Elegir el mejor momento para el tratamiento",
  timingParagraphs: [
    "Aunque la edad a la que los niños reciben brackets puede variar, el tratamiento ortodóntico suele comenzar entre los 9 y los 14 años. Algunos problemas ortodónticos son más fáciles de corregir si se tratan temprano.",
    "Si la Dra. Garcia-Paul cree que tu hijo necesitará tratamiento, te avisará cuándo será mejor comenzarlo. Esto le da a tu ortodoncista la mejor oportunidad de:",
  ],
  timingList: [
    "Guiar el crecimiento de la mandíbula",
    "Disminuir el riesgo de trauma en los dientes frontales prominentes",
    "Corregir hábitos orales perjudiciales",
    "Mejorar la apariencia y la autoestima de tu hijo",
    "Guiar los dientes permanentes hacia una posición más favorable",
    "Crear una disposición más agradable de dientes, labios y rostro",
  ],
  signsHeading: "Señales de que la mordida no está bien",
  signsParagraphs: [
    "No siempre es fácil saber cuándo tu hijo puede necesitar brackets o tener un problema ortodóntico. Incluso los dientes que se ven rectos pueden estar ocultando un problema de mordida.",
    "Estas son algunas señales que pueden indicar la necesidad de atención ortodóntica:",
  ],
  signsList: [
    "Pérdida temprana o tardía de dientes de leche",
    "Dificultad para masticar o morder",
    "Respirar por la boca",
    "Chuparse el dedo",
    "Dientes apiñados, fuera de lugar o bloqueados",
    "Mandíbulas demasiado adelantadas o retrasadas",
    "Morderse la mejilla o morder el paladar",
    "Dientes protruidos",
    "Dientes superiores e inferiores que no se juntan o se juntan de forma anormal",
    "Apariencia facial desequilibrada",
    "Rechinar o apretar los dientes",
  ],
  consultationHeading: "Obtén más información en tu consulta gratuita",
  consultationParagraphs: [
    [
      "Si tu hijo tiene un problema dental, comunícate con nosotros programando una ",
      { text: "consulta gratuita", to: "/free-consult/" },
      ".",
    ],
    [
      "Nos encantaría hablar sobre las opciones ortodónticas de tu hijo y desarrollar un plan de tratamiento personalizado según sus necesidades y deseos.",
    ],
  ],
  faqHeading:
    "Preguntas frecuentes sobre problemas ortodónticos en niños",
  faqItems: [
    {
      question: "¿Cuándo debería ver mi hijo a un ortodoncista por primera vez?",
      answers: [
        "La American Association of Orthodontists recomienda que todos los niños sean examinados por un especialista en ortodoncia a los 7 años.",
        "Una evaluación temprana por un ortodoncista puede ayudar a asegurar que los posibles problemas se detecten pronto y se atiendan antes de que progresen significativamente.",
      ],
    },
    {
      question: "¿Por qué mi hijo necesitaría tratamiento ortodóntico tan temprano?",
      answers: [
        "Algunos problemas ortodónticos se tratan con más facilidad cuando se detectan temprano. En Miami Orthodontic Specialists, evaluaremos qué es lo más apropiado.",
        "En ciertos casos, el tratamiento interceptivo o de Fase I puede evitar que tu hijo necesite un tratamiento más invasivo cuando sea mayor. A los 7 años, los primeros molares e incisivos permanentes generalmente ya han erupcionado.",
        "Esto lo convierte en el momento perfecto para evaluar si tu hijo puede estar desarrollando mordida cruzada, sobremordida, submordida, dientes impactados o apiñamiento severo.",
      ],
    },
    {
      question: "¿Qué pasa si pospongo el tratamiento ortodóntico de mi hijo?",
      answers: [
        "El problema de esperar a que todos los dientes permanentes erupcionen cuando un niño tiene apiñamiento severo o una discrepancia mandibular es la posibilidad de perder potencial de crecimiento, lo que puede llevar a un resultado comprometido que quizá no sea estable.",
        "El tratamiento a una edad posterior puede requerir un plan más agresivo para lograr un resultado satisfactorio.",
      ],
    },
    {
      question: "¿Todos los niños necesitan tratamiento ortodóntico tan temprano?",
      answers: [
        "¡No! De hecho, la mayoría de los padres se sienten aliviados después de que evaluamos a su hijo y descubren que no necesita tratamiento ortodóntico de inmediato.",
        "Si tu hijo no está listo para el tratamiento, lo colocaremos en un programa de observación donde será examinado a intervalos regulares para asegurarnos de que su desarrollo dental y crecimiento facial van por buen camino.",
      ],
    },
  ],
  closingHeading:
    "Si tu hijo tiene 7 años o más, llama a nuestra oficina hoy para programar tu consulta de cortesía.",
  closingParagraph:
    "Ofrecemos este examen inicial sin costo porque sabemos lo importante que es que comprendas completamente las necesidades y opciones de tratamiento de tu hijo antes de invertir en atención ortodóntica.",
  inlineImageAlt: "Niña sonriendo con brackets y una rebanada de sandía",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

function RichText({ parts }: { parts: RichParagraph }) {
  return (
    <Text as="p" size="5" className={styles.paragraph}>
      {parts.map((part) =>
        typeof part === "string" ? (
          part
        ) : (
          <Link key={`${part.to}-${part.text}`} to={part.to} className={styles.link}>
            {part.text}
          </Link>
        ),
      )}
    </Text>
  );
}

function paragraphKey(parts: RichParagraph) {
  return parts
    .map((part) => (typeof part === "string" ? part : `${part.to}:${part.text}`))
    .join("");
}

function ParagraphList({ paragraphs }: { paragraphs: string[] }) {
  return (
    <>
      {paragraphs.map((paragraph) => (
        <Text as="p" key={paragraph} size="5" className={styles.paragraph}>
          {paragraph}
        </Text>
      ))}
    </>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className={styles.list}>
      {items.map((item) => (
        <li key={item}>
          <Text as="span" size="5">
            {item}
          </Text>
        </li>
      ))}
    </ul>
  );
}

export function OrthodonticsForChildrenPage() {
  const getLocalization = useLocalization(localized);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const answerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  useEffect(() => {
    answerRefs.current.forEach((ref, index) => {
      if (!ref) {
        return;
      }

      ref.style.height =
        index === expandedFaq ? `${ref.scrollHeight}px` : "0px";
    });
  }, [expandedFaq]);

  return (
    <>
      <HeroStyle2
        heroImageUrl="/miami-ortho-4.jpg"
        title={getLocalization("title")}
        backgroundPosition="center"
      />

      <Box className={styles.introSection}>
        <Flex className={styles.introContainer} direction="column" gap="5">
          <Heading as="h2" className={styles.introHeading}>
            {getLocalization("introHeading")}
          </Heading>
          {getLocalization("introParagraphs").map((paragraph) => (
            <RichText key={paragraphKey(paragraph)} parts={paragraph} />
          ))}
        </Flex>
      </Box>

      <Box className={styles.contentSection}>
        <Flex className={styles.contentContainer} direction="column" gap="8">
          <Flex direction="column" gap="3">
            <Heading as="h3" className={styles.sectionHeading}>
              {getLocalization("checkupHeading")}
            </Heading>
            {getLocalization("checkupParagraphs").map((paragraph) => (
              <RichText key={paragraphKey(paragraph)} parts={paragraph} />
            ))}
          </Flex>

          <OptimizedImage
            className={styles.inlineImage}
            src="/miami-ortho-17.jpg"
            alt={getLocalization("inlineImageAlt")}
          />

          <Grid columns="12" gap="7">
            <Flex
              direction="column"
              gap="3"
              gridColumn={{ initial: "1 / 13", md: "1 / 7" }}
            >
              <Heading as="h4" className={styles.subheading}>
                {getLocalization("earlyTreatmentHeading")}
              </Heading>
              <ParagraphList
                paragraphs={getLocalization("earlyTreatmentParagraphs")}
              />
            </Flex>
            <Flex
              direction="column"
              gap="3"
              gridColumn={{ initial: "1 / 13", md: "7 / 13" }}
            >
              <Heading as="h4" className={styles.subheading}>
                {getLocalization("timingHeading")}
              </Heading>
              <ParagraphList paragraphs={getLocalization("timingParagraphs")} />
              <BulletList items={getLocalization("timingList")} />
            </Flex>
          </Grid>

          <Grid columns="12" gap="7">
            <Flex
              direction="column"
              gap="3"
              gridColumn={{ initial: "1 / 13", md: "1 / 7" }}
            >
              <Heading as="h4" className={styles.subheading}>
                {getLocalization("signsHeading")}
              </Heading>
              <ParagraphList paragraphs={getLocalization("signsParagraphs")} />
              <BulletList items={getLocalization("signsList")} />
            </Flex>
            <Flex
              direction="column"
              gap="3"
              gridColumn={{ initial: "1 / 13", md: "7 / 13" }}
            >
              <Heading as="h4" className={styles.subheading}>
                {getLocalization("consultationHeading")}
              </Heading>
              {getLocalization("consultationParagraphs").map((paragraph) => (
                <RichText key={paragraphKey(paragraph)} parts={paragraph} />
              ))}
            </Flex>
          </Grid>
        </Flex>
      </Box>

      <Box className={styles.faqSection}>
        <Box className={styles.faqContainer}>
          <Heading as="h2" className={styles.faqHeading}>
            {getLocalization("faqHeading")}
          </Heading>
          {getLocalization("faqItems").map((faq, index) => (
            <div key={faq.question} className={styles.faqItem}>
              <button
                type="button"
                className={styles.faqQuestion}
                onClick={() => toggleFaq(index)}
                aria-expanded={expandedFaq === index}
              >
                {faq.question}
                <span
                  className={`${styles.faqPlusIcon} ${
                    expandedFaq === index ? styles.faqPlusIconOpen : ""
                  }`}
                >
                  +
                </span>
              </button>
              <div
                ref={(el) => {
                  answerRefs.current[index] = el;
                }}
                className={`${styles.faqAnswer} ${
                  expandedFaq === index ? styles.faqAnswerOpen : ""
                }`}
              >
                <Flex direction="column" gap="4">
                  {faq.answers.map((answer) => (
                    <Text
                      as="p"
                      key={answer}
                      size="5"
                      className={styles.paragraph}
                    >
                      {answer}
                    </Text>
                  ))}
                </Flex>
              </div>
            </div>
          ))}
        </Box>
      </Box>

      <Box className={styles.closingSection}>
        <Flex className={styles.closingContainer} direction="column" gap="5">
          <Heading as="h3" className={styles.closingHeading}>
            {getLocalization("closingHeading")}
          </Heading>
          <Text as="p" size="5" className={styles.paragraph}>
            {getLocalization("closingParagraph")}
          </Text>
        </Flex>
      </Box>

      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
